import { FormEvent, useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { changePassword } from '../services/authService';
import { useToast } from '../hooks/useToast';

export const ChangePasswordPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required.';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'New password must be at least 8 characters.';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password.';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      newErrors.newPassword = 'New password must be different from current password.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    if (!token) {
      showToast('You must be logged in to change your password.', 'error');
      return;
    }

    setLoading(true);

    try {
      await changePassword(token, currentPassword, newPassword);
      showToast('Password changed successfully.', 'success');
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage = err.message;
        // Check if it's a validation error for current_password
        if (errorMessage.toLowerCase().includes('current password') || errorMessage.toLowerCase().includes('incorrect')) {
          setErrors({ currentPassword: errorMessage });
        } else {
          showToast(errorMessage, 'error');
        }
      } else {
        showToast('Failed to change password. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout title="Change Password">
      <section className="card" style={{ padding: 28 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 500 }}>
          <div className="form-group">
            <label htmlFor="current-password">Current Password</label>
            <input
              id="current-password"
              type="password"
              className="input"
              autoComplete="current-password"
              placeholder="Enter your current password"
              value={currentPassword}
              onChange={(event) => {
                setCurrentPassword(event.target.value);
                if (errors.currentPassword) {
                  setErrors((prev) => ({ ...prev, currentPassword: '' }));
                }
              }}
              required
            />
            {errors.currentPassword && <div className="error" style={{ marginTop: 8 }}>{errors.currentPassword}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              className="input"
              autoComplete="new-password"
              placeholder="Enter your new password (min. 8 characters)"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
                if (errors.newPassword) {
                  setErrors((prev) => ({ ...prev, newPassword: '' }));
                }
                // Clear confirm password error if passwords now match
                if (confirmPassword && event.target.value === confirmPassword && errors.confirmPassword) {
                  setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                }
              }}
              required
              minLength={8}
            />
            {errors.newPassword && <div className="error" style={{ marginTop: 8 }}>{errors.newPassword}</div>}
            <div className="subtle-text" style={{ marginTop: 6, fontSize: '0.85rem' }}>
              Password must be at least 8 characters long.
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              className="input"
              autoComplete="new-password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                if (errors.confirmPassword) {
                  setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                }
              }}
              required
            />
            {errors.confirmPassword && <div className="error" style={{ marginTop: 8 }}>{errors.confirmPassword}</div>}
          </div>

          <button className="btn" type="submit" disabled={loading} style={{ marginTop: 8 }}>
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </section>
    </AppLayout>
  );
};

